import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getStationRequested } from "../../../redux/actions/stations";
import { formatNumber, unFormatNumber } from "../../../helpers/parsing";
import {
  getProductionDataRequested,
  getProductionDataSucceeded,
} from "../../../redux/actions/productionData";
import { convertAmountToFrontend } from "../../../helpers/convert";
import { setNotification } from "../../../helpers/notification";
import moment from "moment";
import Button from "../../../components/Button";
import ComboBox from "../../../components/ComboBox";
import DataTable from "../../../components/DataTable";
import { Input, Select } from "../../../components/Inputs";
import Modal from "../../../components/Modal";
import ModalConfirmation from "../../../components/Modal/ModalConfirmation";
import TitleValue from "../../../components/TitleValue";
import { API_POST } from "../../../helpers/api";
import Loading from "../../../components/Loading";
import { EmptyState } from "../../../components/EmptyState";
import { resourceTypes } from "../../../constants/selects";
import { monthsAndResourceTypes } from "../../../constants/translations";

export default function ExportTransactions({ t }) {
  const dispatch = useDispatch();
  const productionData = useSelector((state) => state.productionData.list);
  const loading = useSelector((state) => state.productionData.loading);
  const stations = useSelector((state) => state.stations.list);
  const selectedOrganization = useSelector(
    (state) => state.organizations.selectedOrganization
  );
  const [selectedMonth, setSelectedMonth] = useState();
  const [selectedMonth1, setSelectedMonth1] = useState();
  const [selectedMonth2, setSelectedMonth2] = useState();
  const [selectedTerm, setSelectedTerm] = useState();
  const [termDetails, setTermDetails] = useState([]);
  const [exportedDetail, setExportedDetail] = useState({});
  const [exportedDetailModal, setExportedDetailModal] = useState(false);
  const [exportedDetailConfirmationModal, setExportedDetailConfirmationModal] =
    useState(false);
  const [exportQuantity, setExportQuantity] = useState("");
  const [orderedStations, setOrderedStations] = useState([
    {
      name: t("pages.default.exportTransactions.filters.allStations"),
      value: "-",
    },
  ]);
  const [selectedStation, setSelectedStation] = useState("-");
  const [selectedResourceType, setSelectedResourceType] = useState("-");
  const [requestLoading, setRequestLoading] = useState(false);

  const translations = monthsAndResourceTypes(t);
  const selectResourceType = resourceTypes(t);

  const exportColumns = [
    {
      name: t("pages.default.exportTransactions.table.productionPeriod"),
      selector: "month",
      sortable: true,
      cell: (row) => {
        return translations[row.month];
      },
    },
    {
      name: t("pages.default.exportTransactions.table.productionYear"),
      selector: "year",
      sortable: true,
    },
    {
      name: t("pages.default.exportTransactions.table.productionAmount"),
      selector: "amount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.amount);
      },
    },
    {
      name: t(
        "pages.default.exportTransactions.table.documentedProductionAmount"
      ),
      selector: "documentedProductionAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.documentedProductionAmount);
      },
    },
  ];

  const exportDetailColumns = [
    {
      name: t("pages.default.exportTransactions.table.stationId"),
      selector: "stationId",
      width: "100px",
      sortable: true,
    },
    {
      name: t("pages.default.exportTransactions.table.stationName"),
      selector: "stationName",
      width: "150px",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.exportTransactions.table.stationLicenceNo"),
      selector: "stationLicenceNo",
      width: "140px",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.exportTransactions.table.resourceType"),
      selector: "resourceType",
      width: "100px",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.exportTransactions.table.productionAmount"),
      selector: "amount",
      width: "100px",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.amount);
      },
    },
    {
      name: selectedMonth2,
      selector: "remainderAmountA2",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.remainderAmountA2);
      },
    },
    {
      name: selectedMonth1,
      selector: "remainderAmountA1",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.remainderAmountA1);
      },
    },
    {
      name: selectedMonth,
      selector: "remainderAmountA",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.remainderAmountA);
      },
    },
    {
      name: t("pages.default.exportTransactions.table.GDDK"),
      selector: "gddkProductionData",
      sortable: true,
    },
    {
      name: t(
        "pages.default.exportTransactions.table.documentedProductionAmount"
      ),
      selector: "documentedProductionAmount",
      width: "100px",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.documentedProductionAmount);
      },
    },
    {
      name: t("pages.default.exportTransactions.table.process"),
      selector: "id",
      sortable: false,
      width: "120px",
      cell: (row) => {
        return (
          <Button
            title={t("pages.default.exportTransactions.table.export")}
            type="secondary"
            label={t("pages.default.exportTransactions.table.export")}
            onClick={() => exportDetailClick(row)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    let s = [
      {
        name: t("pages.default.exportTransactions.filters.allStations"),
        value: "-",
      },
    ];
    // eslint-disable-next-line array-callback-return
    stations.map((item) => {
      s.push({ name: item.stationName, value: item.stationId });
    });
    setOrderedStations(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stations]);

  useEffect(() => {
    if (checkOrganizationExist()) {
      dispatch(getStationRequested(selectedOrganization.organizationId));
    } else {
      dispatch(getProductionDataSucceeded([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization]);

  // if production data change, change productionDetail
  useEffect(() => {
    exportSelection({ term: selectedTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionData]);

  const checkOrganizationExist = () => {
    if (selectedOrganization) {
      if (!selectedOrganization.organizationId) {
        return false;
      }
      return true;
    }
    return false;
  };

  const getProductionDatas = () => {
    if (checkOrganizationExist()) {
      dispatch(getProductionDataRequested(selectedOrganization.organizationId));
      setSelectedTerm();
    } else {
      setNotification(
        "error",
        t(
          "pages.default.exportTransactions.notifications.selectOrganizationError"
        )
      );
    }
  };

  const groupByTerm = (data) => {
    // deep copy
    let dataCopy = JSON.parse(JSON.stringify(data));
    // group by term
    let groupedByTerm = dataCopy.reduce((acc, value) => {
      let term = `${moment(value.term).format("MM")}-${moment(
        value.term
      ).format("YYYY")}`;
      if (!acc[term]) {
        acc[term] = [];
      }
      acc[term].push(value);
      return acc;
    }, {});
    // term keys
    let terms = Object.keys(groupedByTerm);
    // term sums
    let groupedSums = terms.reduce((acc, value) => {
      let sum = groupedByTerm[value].reduce(
        (termSum, termValue) => {
          let amount = termValue.amount + termSum.amount;
          let documentedProductionAmount = termValue.certifiableAmount
            ? termValue.certifiableAmount
            : 0;
          let documentedGddk = termValue.gddkProductionData
            ? termValue.gddkProductionData.certifiableAmount
            : 0;
          termSum.term = `${moment(termValue.term).format("MM-YYYY")}`;
          termSum.termSort = termValue.term;
          termSum.month = moment(termValue.term).format("M");
          termSum.year = moment(termValue.term).format("YYYY");
          termSum.amount = parseInt(amount);
          termSum.documentedProductionAmount +=
            documentedProductionAmount + documentedGddk;
          return termSum;
        },
        { amount: 0, documentedProductionAmount: 0 }
      );

      sum.documentedProductionAmount = formatNumber(
        sum.documentedProductionAmount
      );

      sum.amount = formatNumber(sum.amount);
      acc.push(sum);
      return acc;
    }, []);
    return groupedSums;
  };
  const groupByTermDetail = (termFilter, data) => {
    // deep copy
    let dataCopy = JSON.parse(JSON.stringify(data));
    // group by term
    let groupedByTerm = dataCopy.reduce((acc, value) => {
      let term = `${moment(value.term).format("MM-YYYY")}`;
      if (!acc[term]) {
        acc[term] = [];
      }
      let documentedProductionAmount = value.certifiableAmount
        ? value.certifiableAmount
        : 0;
      let documentedGddk = value.gddkProductionData
        ? value.gddkProductionData.certifiableAmount
        : 0;
      let gddkAmount = value.gddkProductionData
        ? value.gddkProductionData.amount
        : 0;
      acc[term].push({
        id: value.id,
        stationId: value.station.stationId,
        stationName: value.station.stationName,
        stationLicenceNo: value.station.stationLicenseNo,
        year: moment(value.term).format("YYYY"),
        month: moment(value.term).format("M"),
        amount: formatNumber(parseFloat(value.amount)), // burada
        resourceType: value.resourceType,
        documentedProductionAmount: formatNumber(
          parseInt(documentedProductionAmount + documentedGddk)
        ),
        remainderAmountA: Math.floor(value.remainderAmountA * 10) / 10,
        remainderAmountA1: Math.floor(value.remainderAmountA1 * 10) / 10,
        remainderAmountA2: Math.floor(value.remainderAmountA2 * 10) / 10,
        gddkProductionData: formatNumber(parseFloat(gddkAmount)),
      });

      return acc;
    }, {});
    return groupedByTerm[termFilter];
  };

  const exportSelection = (row) => {
    console.log({ row });
    let term = row.month ?? row?.term?.split('-')[0];
    setSelectedMonth(translations[term]);
    var month1 = term - 1 < 1 ? 12 : term - 1;
    setSelectedMonth1(translations[month1]);
    var month2 = month1 - 1 < 1 ? 12 : month1 - 1;
    setSelectedMonth2(translations[month2]);
    setSelectedTerm(row.term ?? term);
    setTermDetails(groupByTermDetail(row.term, productionData));
  };
  const exportRowSelected = (row) => {
    if (selectedTerm === row.term) return true;
    else return false;
  };
  const filterTermDetails = () => {
    // deep copy
    let data = groupByTermDetail(selectedTerm, productionData);
    data = data.filter((item) => {
      return (
        (selectedStation === "-"
          ? true
          : item.stationId === parseInt(selectedStation)) &&
        (selectedResourceType === "-"
          ? true
          : item.resourceType === selectedResourceType)
      );
    });
    setTermDetails(data);
  };
  const exportDetailClick = (row) => {
    setExportedDetail(row);
    setExportedDetailModal(true);
    setExportQuantity("");
  };
  const quantityChange = (e) => {
    setExportQuantity(parseInt(e.target.value ? e.target.value : 0));
  };
  const exportedDetailRequest = async () => {
    setRequestLoading(true);
    try {
      await API_POST("certificate", {
        organizationId: selectedOrganization.organizationId,
        productionDataId: exportedDetail.id,
        stationId: exportedDetail.stationId,
        resourceType: exportedDetail.resourceType,
        amount: exportQuantity,
        year: exportedDetail.year,
        month: exportedDetail.month,
      });
      // get productionDatas
      dispatch(getProductionDataRequested(selectedOrganization.organizationId));
      // Close modals
      setExportedDetailConfirmationModal(false);
      setExportedDetailModal(false);
      setNotification(
        "success",
        t("pages.default.exportTransactions.notifications.success")
      );
      setRequestLoading(false);
    } catch (err) {
      setRequestLoading(false);
      setNotification("error", err.response.data.message);
    }
  };

  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.exportTransactions.title")}
      </h5>
      <div className="km__p--card">
        <div className="km__p--export-transactions-filter">
          <ComboBox t={t} />
          <Button
            label={t("pages.default.exportTransactions.filter")}
            onClick={getProductionDatas}
          />
        </div>
        {!loading && productionData.length > 0 ? (
          <DataTable
            columns={exportColumns}
            data={groupByTerm(productionData)}
            rowKey={"term"}
            pagination
            paginationPerPage={25}
            selectableRow
            defaultSortAsc={false}
            defaultSortedField={"termSort"}
            rowSelection={(row) => exportSelection(row)}
            isRowSelected={(row) => exportRowSelected(row)}
          />
        ) : null}
        {!loading && productionData.length === 0 ? (
          <EmptyState
            text={t("pages.default.exportTransactions.empty")}
            icon="file-text"
          />
        ) : null}
        {loading ? (
          <Loading text={t("pages.default.exportTransactions.loading")} />
        ) : null}
      </div>

      {productionData.length > 0 && selectedTerm && !loading ? (
        <div className="km__p--card">
          <div className="km__p--export-transactions-filter">
            <Select
              options={orderedStations}
              defaultValue={selectedStation}
              onChange={(e) => setSelectedStation(e.target.value)}
            />
            <Select
              options={selectResourceType}
              defaultValue={selectedResourceType}
              onChange={(e) => setSelectedResourceType(e.target.value)}
            />
            <Button
              label={t("pages.default.exportTransactions.filter")}
              type="primary"
              onClick={filterTermDetails}
            />
          </div>
          <DataTable
            columns={exportDetailColumns}
            data={termDetails}
            rowKey={"id"}
            headerHeading={{
              start: 5,
              end: 7,
              text: t(
                "pages.default.exportTransactions.circulatedProductionAmount"
              ),
            }}
            pagination
            paginationPerPage={25}
          />
          <Modal
            isOpen={exportedDetailModal}
            toggle={() => setExportedDetailModal(!exportedDetailModal)}
          >
            <div className="km__p--export-detail-modal-header">
              {t("pages.default.exportTransactions.modal.title")}
            </div>
            <div className="km__p--export-detail-modal-body">
              <TitleValue
                title={t("pages.default.exportTransactions.table.stationId")}
                value={exportedDetail.stationId}
                size="lg"
              />
              <TitleValue
                title={t("pages.default.exportTransactions.table.stationName")}
                value={exportedDetail.stationName}
                size="lg"
              />
              <Input
                type="number"
                min={1}
                max={unFormatNumber(exportedDetail.documentedProductionAmount)}
                placeholder={exportedDetail.documentedProductionAmount}
                value={exportQuantity}
                label={t("pages.default.exportTransactions.table.amount")}
                onChange={quantityChange}
              />
            </div>
            <div className="km__p--export-detail-modal-footer">
              <Button
                type="secondary"
                label={t("pages.default.exportTransactions.modal.dismiss")}
                onClick={() => setExportedDetailModal(false)}
              />
              <Button
                type="primary"
                label={t("pages.default.exportTransactions.modal.export")}
                onClick={() => setExportedDetailConfirmationModal(true)}
                disabled={!exportQuantity}
              />
            </div>
          </Modal>
          <ModalConfirmation
            isOpen={exportedDetailConfirmationModal}
            toggle={() =>
              setExportedDetailConfirmationModal(
                !exportedDetailConfirmationModal
              )
            }
            approve={exportedDetailRequest}
            body={t("pages.default.exportTransactions.confirmation.body")}
            header={t("pages.default.exportTransactions.confirmation.header")}
            loading={requestLoading}
          />
        </div>
      ) : null}
    </>
  );
}
